import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/Auth/Login`;
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly isAuthenticated = signal(this.checkTokenExists());
  readonly currentUser = signal<{ name: string } | null>(this.getUserFromStorage());

  login(login: string, password: string): Observable<any> {
    return this.http.post<any>(this.apiUrl, { login, password }).pipe(
      tap(response => this.setSession(response))
    );
  }

  getToken(): string | null {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;

    if (this.isTokenExpired(token)) {
      this.logout();
      return null;
    }

    return token;
  }

  isAdmin(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = this.parseToken(token);
      const role = payload['role'] ?? payload['roles'] ?? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return Array.isArray(role) ? role.includes('Admin') : role === 'Admin';
    } catch { return false; }
  }

  logout(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('expiresIn');
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }

  private setSession(response: any): void {
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('expiresIn', String(response.expiresIn));
    this.isAuthenticated.set(true);
    this.currentUser.set(this.decodeToken(response.accessToken));
  }

  private checkTokenExists(): boolean {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;

    if (!this.decodeToken(token) || this.isTokenExpired(token)) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('expiresIn');
      return false;
    }

    return true;
  }

  private getUserFromStorage(): { name: string } | null {
    const token = localStorage.getItem('accessToken');
    return token && !this.isTokenExpired(token) ? this.decodeToken(token) : null;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = this.parseToken(token);
      return payload.exp ? payload.exp * 1000 < Date.now() : false;
    } catch {
      return true;
    }
  }

  private decodeToken(token: string): { name: string } | null {
    try {
      const payload = this.parseToken(token);
      return {
        name: payload.userName || payload.unique_name || 'Usuário',
      };
    } catch {
      return null;
    }
  }

  private parseToken(token: string): any {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), character => character.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  }
}
